import React, { useState } from 'react';
import { audienceOptions } from '../../data/audienceOptions';
import { Pill } from '../ds';
import './AudienceSelector.css';

// Build a flat lookup: value -> { label, group }
const optionLookup = {};
for (const group of audienceOptions) {
  for (const opt of group.options) {
    optionLookup[opt.value] = { label: opt.label, group: group.label };
  }
}

export default function AudienceSelector({ value = [], onChange, label, disabled = false }) {
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState(new Set());

  const toggleOption = (optValue) => {
    if (disabled) return;
    if (value.includes(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  const removeTag = (optValue) => {
    if (disabled) return;
    onChange(value.filter((v) => v !== optValue));
  };

  const toggleGroup = (groupLabel) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupLabel)) next.delete(groupLabel);
      else next.add(groupLabel);
      return next;
    });
  };

  const lowerSearch = search.toLowerCase();

  // Filter groups and options by search
  const filteredGroups = audienceOptions
    .map((group) => ({
      ...group,
      options: group.options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(lowerSearch) ||
          opt.value.toLowerCase().includes(lowerSearch) ||
          group.label.toLowerCase().includes(lowerSearch)
      ),
    }))
    .filter((group) => group.options.length > 0);

  return (
    <div className="audience-v2">
      <label className="audience-v2__label">{label}</label>

      {/* Selected tag chips */}
      {value.length > 0 && (
        <div className="audience-v2__tags">
          {!disabled && (
            <button
              type="button"
              className="audience-v2__clear-all"
              onClick={() => onChange([])}
            >
              全部清除
            </button>
          )}
          {value.map((v) => {
            const info = optionLookup[v];
            return (
              <span key={v} className="audience-v2__tag">
                {info ? info.label : v}
                {!disabled && (
                  <button type="button" className="audience-v2__tag-remove" onClick={() => removeTag(v)}>
                    &times;
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Search box */}
      <div className="audience-v2__search-wrapper">
        <svg className="audience-v2__search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="8.5" cy="8.5" r="5.5" />
          <path d="M12.5 12.5L17 17" />
        </svg>
        <input
          type="text"
          className="audience-v2__search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜尋受眾..."
          disabled={disabled}
        />
      </div>

      {/* Grouped accordion */}
      <div className="audience-v2__groups">
        {filteredGroups.length === 0 && (
          <div className="audience-v2__no-results">沒有符合的結果</div>
        )}
        {filteredGroups.map((group) => {
          const isOpen = openGroups.has(group.label) || lowerSearch.length > 0;
          const selectedCount = group.options.filter((o) => value.includes(o.value)).length;
          return (
            <div key={group.label} className={`audience-v2__group ${isOpen ? 'open' : ''}`}>
              <div className="audience-v2__group-header" onClick={() => toggleGroup(group.label)}>
                <span className="audience-v2__group-title">
                  {group.label}
                  <span className="audience-v2__group-count">{group.options.length} 個</span>
                  {selectedCount > 0 && (
                    <span className="audience-v2__group-selected">已選 {selectedCount}</span>
                  )}
                </span>
                <svg className="audience-v2__group-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 6l4 4 4-4" />
                </svg>
              </div>
              <div className="audience-v2__group-body">
                <div className="audience-v2__options">
                  {group.options.map((opt) => (
                    <Pill
                      key={opt.value}
                      active={value.includes(opt.value)}
                      onClick={() => toggleOption(opt.value)}
                      disabled={disabled}
                    >
                      {opt.label}
                    </Pill>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
